import type { UseQueryResult } from "@tanstack/react-query";
import { useQueries } from "@tanstack/react-query";
import { ChevronRight, FileText, Folder } from "lucide-react";
import type React from "react";
import { getNoteById } from "@/notes/api/notes";
import { useTree } from "@/notes/hooks/useTree";
import type { FileNode } from "../../types";
import "./FolderContentsView.css";

interface FolderContentsViewProps {
	folder: FileNode;
	onSelect: (node: FileNode | null) => void;
}

interface NoteCardProps {
	node: FileNode;
	queryResult?: UseQueryResult<FileNode, Error>;
	onSelect: (node: FileNode | null) => void;
}

const NoteCard: React.FC<NoteCardProps> = ({ node, queryResult, onSelect }) => {
	const detailedNote = queryResult?.data;
	const isLoadingDetail = queryResult ? queryResult.isLoading : true;
	const contentVal = detailedNote?.content || "";
	const charCount = contentVal.length;
	const snippet = contentVal
		? contentVal
				.replace(/[#*`_\-\n\r]/g, " ")
				.substring(0, 120)
				.trim()
		: "";

	return (
		<button
			type="button"
			className="folder-item-card is-file"
			onClick={() => onSelect(node)}
		>
			<div className="card-header">
				<FileText className="item-icon file-icon" size={20} />
				<span className="item-title" title={node.title}>
					{node.title}
				</span>
			</div>

			<div className="card-body">
				{isLoadingDetail ? (
					<div className="snippet-skeleton animate-pulse" />
				) : snippet ? (
					<p className="item-snippet">{snippet}...</p>
				) : (
					<p className="item-snippet empty-preview">Empty note</p>
				)}
				<div className="item-meta">
					{isLoadingDetail ? (
						<div className="meta-skeleton animate-pulse" />
					) : (
						<span>{charCount} chars</span>
					)}
				</div>
			</div>
		</button>
	);
};

export const FolderContentsView: React.FC<FolderContentsViewProps> = ({
	folder,
	onSelect,
}) => {
	const { data: tree } = useTree();
	const items = tree?.filter((node) => node.parentId === folder.id) || [];

	const folders = items
		.filter((node) => node.isFolder)
		.sort((a, b) => a.title.localeCompare(b.title));
	const files = items
		.filter((node) => !node.isFolder)
		.sort((a, b) => a.title.localeCompare(b.title));

	// Fetch full details (with content) for all files in this folder in parallel
	const filesQueries = useQueries({
		queries: files.map((file) => ({
			queryKey: ["getNode", file.id],
			queryFn: () => {
				if (!file.id) throw new Error("No file ID");
				return getNoteById(file.id);
			},
			staleTime: 1000 * 60 * 5, // Cache for 5 mins
		})),
	});

	return (
		<div className="folder-view-container">
			<header className="folder-view-header">
				<div className="folder-header-title">
					<Folder className="folder-header-icon" size={28} />
					<h1>{folder.title}</h1>
				</div>
				<span className="folder-meta">
					{items.length} {items.length === 1 ? "item" : "items"} inside
				</span>
			</header>

			{items.length === 0 ? (
				<div className="folder-empty">
					<p className="empty-title">This folder is empty</p>
					<p className="empty-subtitle">
						Create a new note or folder inside this directory using the sidebar
						actions.
					</p>
				</div>
			) : (
				<div className="folder-contents-sections">
					{folders.length > 0 && (
						<div className="folder-section">
							<h2 className="section-title">Folders</h2>
							<div className="folders-grid">
								{folders.map((node) => (
									<button
										key={node.id}
										type="button"
										className="folder-item-card is-folder"
										onClick={() => onSelect(node)}
									>
										<Folder className="item-icon folder-icon" size={20} />
										<span className="item-title" title={node.title}>
											{node.title}
										</span>
										<ChevronRight className="folder-arrow" size={16} />
									</button>
								))}
							</div>
						</div>
					)}

					{files.length > 0 && (
						<div className="folder-section">
							<h2 className="section-title">Notes</h2>
							<div className="notes-grid">
								{files.map((node, index) => (
									<NoteCard
										key={node.id}
										node={node}
										queryResult={filesQueries[index]}
										onSelect={onSelect}
									/>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
